import React, { useEffect, useState } from "react";
import { Grid, Button, Stack, Typography } from "@mui/material";
import { importCSV } from "../lib/importCsv.ts";
import { postArticles } from "../usecases/postArticles.ts";
import { transformFromCSVToArticle } from "../lib/transform/transformFromCsvToArticle.ts";
import { MuiFileInput } from "mui-file-input";
import { extractZipContents } from "../lib/extractZipContents.ts";
import {
  IImportProgressInformation,
  ImportProgressModal,
  ImportProgressStatus,
  ImportType,
} from "./parts/ImportProgressModal.tsx";
import { formatBodyForArticle } from "../lib/formatBodyForArticle.ts";
import { ICSVFormat } from "../models/csv_format.ts";
import { ILazyAttachment } from "../models/zip_content.ts";
import { validateCSVFormat } from "../lib/validate/validateCSVFormat.ts";
import { validateAttachments } from "../lib/validate/validateAttachments.ts";
import { validateInlineImages } from "../lib/validate/validateInlineImages.ts";
import { zendeskHttpRequest } from "../lib/api/zendeskHttpRequest.ts";
import { IUser } from "../models/user.ts";

export const Import: React.FC<{ token: string; selectBrandUrl: string }> = ({
  token,
  selectBrandUrl,
}) => {
  const [userRole, setUserRole] = useState<string | null>(null);
  const [zipFile, setZipFile] = useState<File | null>(null);
  const [csvData, setCsvData] = useState<ICSVFormat[]>([]);
  const [attachments, setAttachments] = useState<ILazyAttachment[]>([]);
  const [inlineImages, setInlineImages] = useState<ILazyAttachment[]>([]);
  const [visibleModal, setVisibleModal] = useState<boolean>(false);
  const [totalArticlesCount, setTotalArticlesCount] = useState<number>(0);
  const [importProgressInfo, setImportProgressInfo] =
    useState<IImportProgressInformation>({ total: 0, imported: 0, logs: [] });
  const [errorMessage, setErrorMessage] = useState<string>("");

  useEffect(() => {
    const fetchUserRole = async () => {
      const data = await zendeskHttpRequest<{ user: IUser }>(
        "users/me.json",
        token,
        "GET",
      );
      setUserRole(data.user.role);
    };
    fetchUserRole().catch(console.error);
  }, [token]);

  const handleFileUpload = async (newFile: File | null) => {
    if (userRole !== "admin") {
      setErrorMessage("権限がありません。");
      return;
    }
    setZipFile(newFile);
    setErrorMessage("");
    setCsvData([]);
    setAttachments([]);
    setInlineImages([]);

    if (newFile) {
      try {
        const { csvFile, attachments, inlineImages } =
          await extractZipContents(newFile);
        const importedCsvData = await importCSV(csvFile);

        console.log(attachments);
        importedCsvData.forEach((csv, index) => {
          try {
            validateCSVFormat(csv);
            validateAttachments(csv, attachments);
            validateInlineImages(csv, inlineImages);
          } catch (error) {
            if (error instanceof Error) {
              error.message = `import.csv ${index + 1}行目: ` + error.message;
            }
            throw error;
          }
        });

        setCsvData(importedCsvData);
        setAttachments(attachments);
        setInlineImages(inlineImages);
        setErrorMessage("");
      } catch (error) {
        if (error instanceof Error) {
          setErrorMessage(error.message);
        } else {
          setErrorMessage(
            "インポートファイルの読み取り時に不明なエラーが発生しました。",
          );
        }
      }
    }
  };

  const handleImportProgressNewArticle = (index: number, title: string, type: ImportType) => {
    setImportProgressInfo((prevInfo) => {
      const newLogs = [...prevInfo.logs];
      newLogs[index] = {
        title: title,
        type: type,
        status: "Import",
        message: "",
        link: "",
      };
      return { ...prevInfo, logs: newLogs };
    });
  };

  const handleImportProgressUpdateStatus = (
    index: number,
    status: ImportProgressStatus,
    message: string,
    link: string,
    type?: ImportType,
  ) => {
    setImportProgressInfo((prevInfo) => {
      const newLogs = [...prevInfo.logs];
      const currentLog = newLogs[index];
      if (!currentLog) return prevInfo;
      newLogs[index] = {
        ...currentLog,
        status: status,
        message: message,
        link: currentLog.link ? currentLog.link : link,
        ...(type && { type }),
      };
      return {
        ...prevInfo,
        logs: newLogs,
      };
    });
  };

  return (
    <Grid>
      <Stack spacing={2}>
        {userRole === "admin" && (
          <>
            <Grid container spacing={2}>
              <Grid item xs={8}>
                <MuiFileInput
                  value={zipFile}
                  placeholder="アップロードするZIPファイルを選択してください"
                  onChange={handleFileUpload}
                  inputProps={{ accept: ".zip" }}
                ></MuiFileInput>
              </Grid>
              <Grid item xs={4}>
                <Button
                  variant="contained"
                  style={{ marginTop: "16px" }}
                  onClick={async () => {
                    if (errorMessage || csvData.length === 0) return;
                    setVisibleModal(true);
                    const articles = csvData.map((data) => {
                      return transformFromCSVToArticle(
                        data,
                        formatBodyForArticle,
                      );
                    });
                    setTotalArticlesCount(articles.length);
                    await postArticles(
                      token,
                      selectBrandUrl,
                      articles,
                      attachments,
                      inlineImages,
                      handleImportProgressNewArticle,
                      handleImportProgressUpdateStatus,
                    );
                  }}
                  sx={{
                    backgroundColor: "#00897B",
                    "&:hover": { backgroundColor: "#00695C" },
                  }}
                  disabled={!!errorMessage || csvData.length === 0}
                >
                  インポート実行
                </Button>
              </Grid>
            </Grid>
            {errorMessage && (
              <Typography color="error">{errorMessage}</Typography>
            )}
          </>
        )}
      </Stack>
      <ImportProgressModal
        open={visibleModal}
        onClose={() => {
          setVisibleModal(false);
          setTotalArticlesCount(0);
          setImportProgressInfo({ total: 0, imported: 0, logs: [] });
        }}
        totalCount={totalArticlesCount}
        info={importProgressInfo}
      />
    </Grid>
  );
};
